import {
  ContentHotel,
  FAQResponse,
  HotelOfferEntity as SapiHotelOfferEntity,
  HotelRoomsResponse,
  ReviewsResponseV2,
  RoomsResults,
  SapiClient,
  SearchResults,
  Suggestion,
  SuggestType
} from '@findhotel/sapi'
import {InsightsResponse} from '@findhotel/sapi/dist/types/packages/core/src/insights'
import {SearchHandler} from '@findhotel/sapi/dist/types/packages/core/src/search'
import {createApi, fakeBaseQuery} from '@reduxjs/toolkit/query/react'
import {v4 as uuid} from 'uuid'

import {
  type MappingContext,
  type OldHotelOfferEntity,
  sapiHotelOfferEntityToHotelOfferEntity
} from '../../../offer/business/offersMapping'
import {numberOfRooms as getNumberOfRooms} from '../../../room/business/roomConfiguration'
import {Room, SplitBooking} from '../../../room/types/room'
import {makeSerializeQueryArgs} from '../../../utils/redux'
import {
  faqFetchSucceeded,
  offersSearchErrored,
  offersSearchReceived,
  offersSearchStarted,
  offersSearchSucceeded,
  roomSearchErrored,
  roomSearchStarted,
  roomSearchSucceeded,
  searchAnchorReceived,
  SearchApiGetSearchActionsType,
  searchCompleted,
  searchErrored,
  searchHotelsReceived,
  searchOffersReceived,
  searchStarted
} from './action'
import {SearchApiError} from './error'
import {
  type TransformedSearchResults,
  transformSearchResults
} from './transformations/transformSearchResults'
import {
  type BaseMappingContext,
  mergeDataRecipe,
  mergeHotelIds,
  sortMosaicImages
} from './utils'

export * from './error'
export {hasSearchResults} from './utils'

declare let Sentry: typeof import('@sentry/browser')

interface BaseQueryParams {
  useSingleCacheKey?: boolean
  bookingHint?: string
}

export type GetSuggestionsParams = {
  query: string
  suggestsCount?: number
  suggestsTypes?: SuggestType[]
}

export interface GetHotelParams extends BaseQueryParams {
  hotelId: string
}

interface GetReviewsParam {
  hotelId: string
  language?: string
  filters?: {
    ratings?: number[]
    fromDate?: string
    toDate?: string
  }
  pageSize?: number
  offset?: number
  search?: string[]
  sortBy?: string
}

export type TransformOffersResponse = (
  response: OldHotelOfferEntity | undefined
) => OldHotelOfferEntity

export type GetOffersParams = Parameters<SapiClient['offers']>[0] &
  MappingContext &
  BaseQueryParams & {
    shouldDispatchActions?: boolean
    /** Extra fail-safe mechanism to prevent unwanted updates to state.sapiSearch.hotelOfferEntities */
    shouldUpdateOfferEntities?: boolean
    /**
     * Optional callback function to transform the response data from the API.
     * This function takes the mapped offer entity as an argument and returns
     * the transformed data. If not provided, the original mapped offer entity
     * will be used directly in the response.
     */
    transformResponse?: TransformOffersResponse
  }

export enum OffersState {
  Pending = 'pending',
  OffersRequested = 'offersRequested',
  OffersReceived = 'offersReceived',
  Completed = 'completed'
}

interface OffersData {
  searchId?: string
  checkIn?: string
  checkOut?: string
  status: OffersState
  offerEntity?: OldHotelOfferEntity
}

interface GetHotelOffers {
  hotelIds: string[]
  checkIn: string
  checkOut: string
  rooms: string
  roomsNumber: number
  includeTaxes: boolean
  includeLocalTaxes: boolean
  cugDeals: string[]
  tier: string
}

type GetHotelOffersResponse = Record<string, OldHotelOfferEntity> | undefined

interface GetHotels {
  hotelIds: string[]
}

interface GetHotelRooms {
  hotelId: string
  attributes?: string[]
}

export interface TransformedHotelRoomsResponse
  extends Omit<HotelRoomsResponse, 'rooms'> {
  rooms: Room[]
}

export interface TransformedRoomsResponse
  extends Omit<RoomsResults, 'rooms' | 'splitBooking'> {
  rooms: Room[]
  searchId: string
  splitBooking?: SplitBooking
  status?: {
    complete: boolean
    /** True when a refetch is triggered (via polling interval or manual refetch) */
    isRefreshing?: boolean
  }
}

export type TransformRoomsResponse = (
  response: RoomsResults,
  params: BaseGetRoomsParams
) => TransformedRoomsResponse

export type BaseGetRoomsParams = Parameters<SapiClient['rooms']>[0] &
  BaseQueryParams & {
    isPreClickOfferCheckValidationEnabled?: boolean
    /** Set to false to skip the room-search redux actions (and the RoomSearch analytics built from them). Defaults to true. */
    shouldDispatchActions?: boolean
  }

export type GetRoomsParams = BaseGetRoomsParams & {
  transformResponse: TransformRoomsResponse
}

export type GetSearchParams = Parameters<SapiClient['search']>[0] &
  BaseQueryParams & {
    shouldDispatchActions?: boolean
    queryContext?: Record<string, unknown> // Tracked with errors to help isolate issues
    mappingContext: BaseMappingContext
  }

export enum SearchState {
  Pending = 'pending',
  HotelsPending = 'hotelsPending',
  OffersPending = 'offersPending',
  Completed = 'completed'
}

export interface SearchData extends Partial<TransformedSearchResults> {
  searchState?: SearchState
  currentSearch?: Awaited<ReturnType<SearchHandler>>
}

/**
 * Initial state for search data
 */
export const initialSearchData: SearchData = {
  searchState: SearchState.Pending,
  hotelEntities: {},
  hotelOfferEntities: {},
  hotelIds: []
}

interface GetFreeTextSuggestParams {
  query: string
}

/**
 * Interface for the extra argument provided to RTK Query endpoints.
 * Consuming stores must provide an implementation of getSapiClient.
 */
export interface SearchApiThunkExtra {
  getSapiClient: () => Promise<SapiClient>
}

function ensureSearchApiExtra(extra: unknown): SearchApiThunkExtra {
  const typed = extra as SearchApiThunkExtra
  if (!typed?.getSapiClient) {
    throw new Error(
      'searchApi requires getSapiClient in store extraArgument. ' +
        'Ensure your store provides: thunk: { extraArgument: { getSapiClient } }'
    )
  }
  return typed
}

// baseQuery is unused because we use the SAPI SDK but the error type is enforced for queryFn
/**
 * Base query configuration for the search API.
 * This is unused since we use the SAPI SDK directly, but the error type is enforced for queryFn.
 */
const baseQuery = fakeBaseQuery<SearchApiError>()

/**
 * Redux Toolkit Query API definition for search-related endpoints
 */
export const searchApi = createApi({
  reducerPath: 'searchApi',
  baseQuery,
  endpoints: builder => ({
    getHotel: builder.query<ContentHotel | undefined, GetHotelParams>({
      queryFn: async ({hotelId}, {extra}) => {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        try {
          const sapiClient = await getSapiClient()
          const hotel = await sapiClient.hotel(hotelId)
          const transformed: ContentHotel = {
            ...hotel,
            images: sortMosaicImages(hotel.images)
          }
          return {data: transformed}
        } catch (error) {
          return {
            error: new SearchApiError('Error fetching hotel', error)
          }
        }
      }
    }),
    getRoomsPolling: builder.query<TransformedRoomsResponse, GetRoomsParams>({
      queryFn: async (parameters, {forced}) => {
        return {
          data: {
            anonymousId: '',
            rooms: [],
            searchId: uuid(),
            hotelId: parameters.hotelId,
            status: {
              complete: false,
              // RTK Query sets forced=true on refetch (polling interval or manual)
              isRefreshing: forced ?? false
            }
          }
        }
      },
      serializeQueryArgs: makeSerializeQueryArgs<BaseQueryParams>({
        ignoreKeys: [
          'transformResponse',
          'searchId',
          'isPreClickOfferCheckValidationEnabled',
          'shouldDispatchActions'
        ]
      }),
      async onQueryStarted(
        params,
        {dispatch, updateCachedData, getCacheEntry, queryFulfilled, extra}
      ) {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        let isPollingRefresh = false

        try {
          const {data} = await queryFulfilled
          isPollingRefresh = data?.status?.isRefreshing ?? false
        } catch {
          return
        }

        const roomsRequestId = uuid()

        try {
          const {transformResponse, shouldDispatchActions = true} = params
          const sapiClient = await getSapiClient()

          await sapiClient.rooms(params, {
            onStart: () => {
              if (shouldDispatchActions) {
                dispatch(roomSearchStarted({roomsRequestId}))
              }
            },
            onOffersReceived: response => {
              updateCachedData(draft => ({
                ...draft,
                ...transformResponse(response as RoomsResults, params)
              }))
            },
            onComplete: response => {
              updateCachedData(draft => ({
                ...draft,
                status: {
                  complete: response.status?.complete ?? true,
                  isRefreshing: false
                }
              }))
              if (shouldDispatchActions) {
                dispatch(
                  roomSearchSucceeded({
                    params,
                    isRefreshing: isPollingRefresh,
                    roomsRequestId,
                    isPreClickOfferCheckValidationEnabled:
                      params.isPreClickOfferCheckValidationEnabled
                  })
                )
              }
            }
          })
        } catch (originalError) {
          const {data} = getCacheEntry()
          const error = new SearchApiError(
            'Error fetching rooms long polling',
            originalError
          )
          // Reset isRefreshing on error to prevent stuck loading state
          if (isPollingRefresh) {
            updateCachedData(draft => ({
              ...draft,
              status: {
                ...draft.status,
                complete: draft.status?.complete ?? false,
                isRefreshing: false
              }
            }))
          }
          if (params.shouldDispatchActions ?? true) {
            dispatch(roomSearchErrored({roomsRequestId, params, data, error}))
          }
          if (typeof Sentry !== 'undefined') {
            Sentry.captureException(error, {extra: {params}})
          }
        }
      }
    }),
    getOffers: builder.query<OffersData, GetOffersParams>({
      // Initial data
      queryFn() {
        return {
          data: {
            status: OffersState.Pending
          }
        }
      },
      serializeQueryArgs: makeSerializeQueryArgs<BaseQueryParams>({
        ignoreKeys: ['shouldDispatchActions', 'transformResponse']
      }),
      async onCacheEntryAdded(
        parameters,
        {
          updateCachedData,
          cacheDataLoaded,
          cacheEntryRemoved,
          dispatch,
          getCacheEntry,
          extra
        }
      ) {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }

        try {
          const {
            transformResponse,
            shouldDispatchActions,
            ...getOffersParameters
          } = parameters

          const mapOfferEntityV3ToOldDataModel = (
            offerEntity?: SapiHotelOfferEntity
          ) => {
            if (!offerEntity) return undefined
            const cacheEntry = getCacheEntry()
            const numberOfRooms = offerEntity.roomsConfig
              ? getNumberOfRooms(offerEntity.roomsConfig)
              : parameters.numberOfRooms
            const mappedOfferEntity = sapiHotelOfferEntityToHotelOfferEntity(
              offerEntity,
              {
                // Prefer using dates returned by SAPI
                checkIn: cacheEntry.data?.checkIn || parameters.checkIn,
                checkOut: cacheEntry.data?.checkOut || parameters.checkOut,
                includeLocalTaxes: parameters.includeLocalTaxes,
                includeTaxes: parameters.includeTaxes,
                includeRoomsInNightlyPrice:
                  parameters.includeRoomsInNightlyPrice,
                numberOfRooms
              }
            )

            return transformResponse
              ? transformResponse(mappedOfferEntity)
              : mappedOfferEntity
          }
          const sapiClient = await getSapiClient()
          await sapiClient.offers(getOffersParameters, {
            onStart: ({searchId, checkIn, checkOut, rooms}) => {
              updateCachedData(() => ({
                searchId,
                // Store actual dates used by SAPI (eg. defaults or modified)
                checkIn: checkIn ?? parameters.checkIn,
                checkOut: checkOut ?? parameters.checkOut,
                status: OffersState.OffersRequested
              }))
              if (shouldDispatchActions) {
                dispatch(
                  offersSearchStarted({
                    parameters,
                    searchId,
                    rooms,
                    checkIn,
                    checkOut
                  })
                )
              }
            },
            onOffersReceived: offerEntity => {
              updateCachedData(draft => ({
                ...draft,
                offerEntity: mapOfferEntityV3ToOldDataModel(offerEntity),
                status: OffersState.OffersReceived
              }))
              const cacheEntry = getCacheEntry()
              if (shouldDispatchActions) {
                dispatch(
                  offersSearchReceived({
                    parameters,
                    offers: cacheEntry.data?.offerEntity,
                    searchId: cacheEntry.data?.searchId as string
                  })
                )
              }
            },
            onComplete: offerEntity => {
              updateCachedData(draft => ({
                ...draft,
                offerEntity: mapOfferEntityV3ToOldDataModel(offerEntity),
                status: OffersState.Completed
              }))
              const cacheEntry = getCacheEntry()
              if (shouldDispatchActions && cacheEntry.data) {
                dispatch(
                  offersSearchSucceeded({
                    parameters,
                    searchId: cacheEntry.data?.searchId as string,
                    offers: cacheEntry.data?.offerEntity
                  })
                )
              }
            }
          })
        } catch (error) {
          const cacheEntry = getCacheEntry()
          dispatch(
            offersSearchErrored({
              parameters,
              searchId: cacheEntry.data?.searchId as string,
              error: new SearchApiError('Error fetching offers', error)
            })
          )
          if (typeof Sentry !== 'undefined') {
            Sentry.captureException(
              new SearchApiError('Error fetching offers', error)
            )
          }
        }

        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
      }
    }),
    getSearch: builder.query<SearchData, GetSearchParams>({
      queryFn() {
        return {data: initialSearchData}
      },
      serializeQueryArgs: makeSerializeQueryArgs<BaseQueryParams>({
        ignoreKeys: ['shouldDispatchActions', 'queryContext']
      }),
      async onCacheEntryAdded(
        parameters,
        {
          updateCachedData,
          cacheDataLoaded,
          cacheEntryRemoved,
          getCacheEntry,
          dispatch,
          extra
        }
      ) {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }

        const {
          shouldDispatchActions,
          queryContext, // eslint-disable-line @typescript-eslint/no-unused-vars
          mappingContext,
          ...searchParameters
        } = parameters
        try {
          /**
           * The callbacks responses are a partial of the results object so we need to merge them into a coherent state
           * Note that onHotelsReceived,onOffersReceived,onComplete will be called again after `loadMore`
           * @param results Partial results object to merge into the current state
           * @param searchState Optional search state to update
           */
          const updateData = (
            results: Partial<SearchResults>,
            searchState?: SearchState
          ) => {
            const transformedResults = transformSearchResults(
              results,
              mappingContext
            )

            return updateCachedData(draft =>
              mergeDataRecipe(draft, transformedResults, searchState)
            )
          }

          const dispatchEvent = (action: SearchApiGetSearchActionsType) => {
            if (!shouldDispatchActions) return
            const {data} = getCacheEntry()
            if (data) dispatch(action({parameters, data}))
          }

          const sapiClient = await getSapiClient()
          await sapiClient.search(searchParameters, {
            onStart: response => {
              updateData(response, SearchState.HotelsPending)
              dispatchEvent(searchStarted)
            },
            onAnchorReceived: response => {
              updateData(response)
              dispatchEvent(searchAnchorReceived)
            },
            onHotelsReceived: response => {
              updateData(response, SearchState.OffersPending)
              dispatchEvent(searchHotelsReceived)
            },
            onOffersReceived: response => {
              updateData(response)
              dispatchEvent(searchOffersReceived)
            },
            onComplete: ({hotelIds}) => {
              const {data} = getCacheEntry()
              const hotelIdsMerged = mergeHotelIds(data?.hotelIds, hotelIds)
              const hotelIdsForHotelsWithOffers = hotelIdsMerged.filter(
                hotelId => data?.hotelOfferEntities?.[hotelId]?.offers?.length
              )
              updateCachedData(draft => {
                draft.searchState = SearchState.Completed
                draft.hotelIds = hotelIdsForHotelsWithOffers
              })
              dispatchEvent(searchCompleted)
            }
          })
        } catch (originalError) {
          const {data} = getCacheEntry()
          const error = new SearchApiError(
            'Error fetching search',
            originalError
          )
          dispatch(searchErrored({parameters, data, error}))
          if (typeof Sentry !== 'undefined')
            Sentry.captureException(error, {extra: {parameters}})
        }

        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
      }
    }),
    getSuggestions: builder.query<
      Suggestion[] | undefined,
      GetSuggestionsParams
    >({
      queryFn: async (parameters, {extra}) => {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        const {query, suggestsCount, suggestsTypes} = parameters
        try {
          const sapiClient = await getSapiClient()
          const data = await sapiClient.suggest(
            query,
            suggestsCount,
            suggestsTypes
          )

          return {data}
        } catch (error) {
          return {
            error: new SearchApiError('Error fetching suggestions', error)
          }
        }
      }
    }),
    getFreeTextSuggest: builder.query<Suggestion[], GetFreeTextSuggestParams>({
      queryFn: async (parameters, {extra}) => {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        const {query} = parameters
        try {
          const sapiClient = await getSapiClient()
          const data = await sapiClient.freeTextSuggest(query)
          return {data}
        } catch (error) {
          return {
            error: new SearchApiError('Error fetching suggestions', error)
          }
        }
      }
    }),
    getUserReviews: builder.query<ReviewsResponseV2, GetReviewsParam>({
      queryFn: async (params, {extra}) => {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        try {
          const sapiClient = await getSapiClient()
          const data = await sapiClient.reviewsV2(params)
          return {data}
        } catch (error) {
          return {
            error: new SearchApiError('Error fetching reviews', error)
          }
        }
      },
      serializeQueryArgs: ({queryArgs}) => {
        // Normalize cache key: exclude offset (for pagination) and default sortBy to null
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {offset, sortBy = null, ...rest} = queryArgs
        return {...rest, sortBy}
      },
      merge: (currentCache, newData, {arg}) => {
        // First page (offset 0) replaces cache, subsequent pages append
        if (!arg.offset) return newData
        return {
          ...newData,
          reviews: [...currentCache.reviews, ...newData.reviews]
        }
      },
      forceRefetch: ({currentArg, previousArg}) =>
        (currentArg?.offset ?? 0) !== (previousArg?.offset ?? 0)
    }),
    getHotels: builder.query<Record<string, ContentHotel>, GetHotels>({
      async queryFn({hotelIds}, {extra}) {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        if (!hotelIds.length) return {data: {}}
        try {
          const sapiClient = await getSapiClient()

          const data = await sapiClient.hotels(hotelIds)

          return {data}
        } catch (error) {
          return {
            error: new SearchApiError('Error fetching hotels', error)
          }
        }
      }
    }),
    getHotelsOffers: builder.query<GetHotelOffersResponse, GetHotelOffers>({
      async queryFn(
        {
          hotelIds,
          checkIn,
          checkOut,
          rooms,
          roomsNumber,
          includeTaxes,
          includeLocalTaxes,
          ...commonSearchParams
        },
        {extra}
      ) {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        try {
          const sapiClient = await getSapiClient()
          const parameters = {
            hotelIds,
            checkIn,
            checkOut,
            rooms,
            ...commonSearchParams
          }

          const data = await sapiClient.hotelsOffers(parameters, {})

          const hotelOfferEntities: GetHotelOffersResponse =
            data &&
            Object.fromEntries(
              Object.entries(data)
                .map(([hotelId, sapiHotelOfferEntity]) => {
                  const offerEntity = sapiHotelOfferEntityToHotelOfferEntity(
                    sapiHotelOfferEntity,
                    {
                      checkIn,
                      checkOut,
                      numberOfRooms: roomsNumber,
                      includeTaxes,
                      includeLocalTaxes,
                      includeRoomsInNightlyPrice: false
                    }
                  )

                  return offerEntity ? [hotelId, offerEntity] : undefined
                })
                .filter(
                  (entry): entry is [string, OldHotelOfferEntity] =>
                    entry !== undefined
                )
            )

          return {data: hotelOfferEntities}
        } catch (error) {
          return {
            error: new SearchApiError('Error fetching hotels offers', error)
          }
        }
      }
    }),
    getHotelRooms: builder.query<TransformedHotelRoomsResponse, GetHotelRooms>({
      async queryFn({hotelId, attributes}, {extra}) {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        if (!hotelId) return {data: {rooms: [], errors: []}}
        try {
          const sapiClient = await getSapiClient()

          const {rooms, errors} = await sapiClient.hotelRooms({
            hotelId,
            attributes
          })

          /**
           * /hotel-rooms describes the same rooms as /rooms but with its own
           * field names, so it is mapped onto `Room` here to keep the room
           * listing components shared between dated and dateless AP.
           * Anything price related (offers, bed types) has no dateless
           * counterpart and stays empty.
           */
          const transformedRooms = rooms.map<Room>(
            ({
              id,
              masterId,
              name,
              description,
              amenities,
              images,
              occupancy,
              roomInfo
            }) => ({
              id,
              masterId,
              name: name ?? '',
              description: description ?? '',
              amenities: [],
              amenitiesList: amenities?.map(({name: title, ...amenity}) => ({
                ...amenity,
                title
              })),
              // Fields outside the requested `attributes` projection are omitted
              images: (images ?? [])
                .filter(({link}) => link?.href)
                // Put the heroImage as the first image on the list
                .sort(
                  (a, b) =>
                    Number(b.heroImage ?? false) - Number(a.heroImage ?? false)
                )
                .map(({link, caption}) => ({url: link.href, caption})),
              occupancy: occupancy && {
                maxAdults: occupancy.maxAllowed.adults,
                maxChildren: occupancy.maxAllowed.children,
                maxOccupancy: occupancy.maxAllowed.total
              },
              occupationPerRoom: occupancy?.maxAllowed.total ?? 0,
              area: roomInfo?.area,
              bedrooms: roomInfo?.bedrooms?.map(({bedConfigurations = []}) => ({
                bedConfigurations: bedConfigurations.map(
                  ({count, type, typeId, cfgId}) => ({
                    count,
                    type,
                    typeID: typeId,
                    cfgID: cfgId
                  })
                )
              })),
              bedTypes: [],
              offers: [],
              squashedIds: []
            })
          )

          return {data: {rooms: transformedRooms, errors}}
        } catch (error) {
          return {
            error: new SearchApiError('Error fetching hotel rooms', error)
          }
        }
      }
    }),
    getFAQ: builder.query<FAQResponse | undefined, {hotelId: string}>({
      queryFn: async ({hotelId}, {dispatch, extra}) => {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        try {
          const sapiClient = await getSapiClient()
          const data = await sapiClient.faq(hotelId)
          // Hotels with nothing generated yet come back without `faqs`, despite the type
          // saying otherwise. That is an empty FAQ, not a failed fetch.
          dispatch(
            faqFetchSucceeded({
              questionsAvailable: (data?.faqs ?? []).map(faq => faq.label)
            })
          )
          return {data}
        } catch (error) {
          return {
            error: new SearchApiError('Error fetching FAQ', error)
          }
        }
      }
    }),
    getAiPoweredInsights: builder.query<
      InsightsResponse | undefined,
      {hotelId: string}
    >({
      queryFn: async ({hotelId}, {extra}) => {
        const {getSapiClient} = ensureSearchApiExtra(extra)
        try {
          const sapiClient = await getSapiClient()
          const data = await sapiClient.insights(hotelId)

          return {data}
        } catch (error) {
          return {
            error: new SearchApiError(
              'Error fetching Ai powered insights',
              error
            )
          }
        }
      }
    })
  })
})

/**
 * Export hooks for interacting with the search API endpoints
 */
export const {
  useGetHotelQuery,
  useGetRoomsPollingQuery,
  useGetOffersQuery,
  useLazyGetOffersQuery,
  useGetSearchQuery,
  useGetSuggestionsQuery,
  useGetFreeTextSuggestQuery,
  usePrefetch,
  useGetUserReviewsQuery,
  useGetHotelRoomsQuery,
  useGetHotelsOffersQuery,
  useGetHotelsQuery,
  useGetFAQQuery,
  useGetAiPoweredInsightsQuery
} = searchApi
